import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * AdminAssetProfilesPage — V22.9.8 Backlog #2
 *
 * Dedicated admin list of every asset profile in the tenant.
 * Shows fan-out counts (assets / vendors / documents / stakeholders /
 * connectivity_links) per profile, supports bulk re-federate by asset_type.
 *
 * Route:  /admin/asset-profiles
 * Access: admin / bcm_manager / department_head
 */
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RefreshCw, Sparkles, Database, Building2, FileText, Users, Cable, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ASSET_TYPE_OPTIONS = [
  { v: "", label: "All asset types" },
  { v: "application", label: "Applications" },
  { v: "business_process", label: "Business Processes" },
  { v: "it_service", label: "IT Services" },
  { v: "facility", label: "Facilities" },
  { v: "vendor", label: "Vendors" },
];

export default function AdminAssetProfilesPage() {
  const { api } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/asset-profiles/admin/list", {
        params: assetTypeFilter ? { asset_type: assetTypeFilter } : {},
      });
      setProfiles(res.data?.profiles || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load profiles"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetTypeFilter]);

  const handleBulkRefederate = async () => {
    if (!window.confirm(
      `Re-federate ${filtered.length} profile(s)? This replays the fan-out — idempotent, no duplicate rows. Audit-logged.`,
    )) return;
    setBulkBusy(true);
    try {
      const payload = assetTypeFilter ? { asset_type: assetTypeFilter } : {};
      const res = await api.post("/asset-profiles/admin/bulk-refederate", payload);
      const ok = (res.data?.results || []).filter(r => r.ok).length;
      toast.success(`Bulk re-federation complete · ${ok}/${res.data?.total || 0} profiles processed`);
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Bulk re-federate failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = profiles.filter((p) =>
    !q || (p.asset_name || "").toLowerCase().includes(q.toLowerCase()) || (p.owner_email || "").toLowerCase().includes(q.toLowerCase())
  );

  const totals = filtered.reduce(
    (acc, p) => {
      acc.profiles += 1;
      Object.entries(p.federated_counts || {}).forEach(([k, v]) => {
        acc[k] = (acc[k] || 0) + (v || 0);
      });
      return acc;
    },
    { profiles: 0 },
  );

  return (
    <div className="space-y-5" data-testid="admin-asset-profiles-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <Sparkles className="w-5 h-5 text-violet-600" />
            Asset Profiles · Admin
          </h1>
          <p className="text-sm text-slate-500">
            Source-of-truth view of every Profile-Gate submission with downstream fan-out counts. V22.9.8.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="admin-asset-profiles-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            type="button"
            onClick={handleBulkRefederate}
            disabled={bulkBusy || filtered.length === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="admin-asset-profiles-bulk-refederate"
          >
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            Bulk Re-federate ({filtered.length})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <StatCard label="Profiles" value={totals.profiles} Icon={Sparkles} tone="violet" />
        <StatCard label="Assets" value={totals.assets || 0} Icon={Database} tone="sky" />
        <StatCard label="Vendors" value={totals.vendors || 0} Icon={Building2} tone="amber" />
        <StatCard label="Documents" value={totals.documents || 0} Icon={FileText} tone="emerald" />
        <StatCard label="Stakeholders" value={totals.stakeholders || 0} Icon={Users} tone="slate" />
        <StatCard label="Connectivity" value={totals.connectivity_links || 0} Icon={Cable} tone="rose" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Profile Catalogue</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search name or owner email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-60 h-8 text-xs"
              data-testid="admin-asset-profiles-search"
            />
            <Select value={assetTypeFilter || "all"} onValueChange={(v) => setAssetTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="admin-asset-profiles-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSET_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.v || "all"} value={o.v || "all"}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No profiles in this tenant{assetTypeFilter ? ` of type "${assetTypeFilter}"` : ""} yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Criticality</TableHead>
                    <TableHead className="text-center">Assets</TableHead>
                    <TableHead className="text-center">Vendors</TableHead>
                    <TableHead className="text-center">Docs</TableHead>
                    <TableHead className="text-center">Conn.</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-profile-row-${p.id}`}>
                      <TableCell className="font-medium">{p.asset_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {(p.asset_type || "").replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{p.owner_email || "—"}</TableCell>
                      <TableCell>
                        <Badge
                          className={`text-[10px] capitalize ${
                            p.business_criticality === "critical" ? "bg-rose-100 text-rose-700" :
                            p.business_criticality === "high" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {p.business_criticality || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-xs">{p.federated_counts?.assets || 0}</TableCell>
                      <TableCell className="text-center text-xs">{p.federated_counts?.vendors || 0}</TableCell>
                      <TableCell className="text-center text-xs">{p.federated_counts?.documents || 0}</TableCell>
                      <TableCell className="text-center text-xs">{p.federated_counts?.connectivity_links || 0}</TableCell>
                      <TableCell className="text-[11px] text-slate-500">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const TONE_CLASS = {
  violet: "bg-violet-50 text-violet-700",
  sky: "bg-sky-50 text-sky-700",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  slate: "bg-slate-50 text-slate-700",
  rose: "bg-rose-50 text-rose-700",
};

const StatCard = ({ label, value, Icon, tone }) => (
  <div className="bg-white border rounded-lg p-3 flex items-center gap-2.5">
    <div className={`w-8 h-8 rounded-md flex items-center justify-center ${TONE_CLASS[tone] || TONE_CLASS.slate}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-lg font-bold text-slate-900 leading-tight">{value}</p>
    </div>
  </div>
);
